// ═══════════════════════════════════════════════════════════
// Public Portal — Account (Register / Login / Dashboard)
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, FormEvent } from 'react';
import { formatDate } from '../../lib/format';
import { Link } from 'react-router-dom';
import {
  IconLoader2, IconUser, IconFileText, IconArrowLeft,
  IconAlertTriangle, IconCreditCard, IconMessageCircle,
  IconCircleCheck, IconChevronRight,
} from '@tabler/icons-react';
import { usePublicAuthStore } from '../../store/publicAuthStore';
import { getMyRequests } from '../../api/publicAuth';
import { FIELDS } from '../../api/fields';

type Mode = 'login' | 'register' | 'dashboard';

const F = FIELDS.requests;

const STATUS_LABELS: Record<string, string> = {
  submitted:         'Submitted',
  acknowledged:      'Acknowledged',
  inProgress:        'In Progress',
  awaitingRequester: 'Response Needed',
  awaitingPayment:   'Payment Required',
  pendingReview:     'Under Review',
  completed:         'Completed',
  denied:            'Denied',
  withdrawn:         'Withdrawn',
  onHold:            'On Hold',
};

const STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  submitted:         { bg: 'var(--cds-cerulean-50)', text: 'var(--cds-blue-700)' },
  acknowledged:      { bg: 'var(--cds-green-50)', text: 'var(--cds-green-700)' },
  inProgress:        { bg: 'var(--cds-blurple-50)', text: 'var(--cds-blurple-700)' },
  awaitingRequester: { bg: 'var(--cds-orange-50)', text: 'var(--cds-terracotta-700)' },
  awaitingPayment:   { bg: 'var(--cds-orange-50)', text: 'var(--cds-terracotta-700)' },
  pendingReview:     { bg: 'var(--cds-violet-50)', text: 'var(--cds-violet-600)' },
  completed:         { bg: 'var(--cds-green-50)', text: '#166534' },
  denied:            { bg: 'var(--cds-red-50)', text: 'var(--cds-red-700)' },
  withdrawn:         { bg: 'var(--cds-gray-50)', text: '#6B7280' },
  onHold:            { bg: 'var(--cds-gray-50)', text: '#475569' },
};

// Action items that require customer attention
const ACTION_STATUSES = new Set(['awaitingPayment', 'awaitingRequester']);

interface ActionBannerProps {
  status: string;
  requestNumber: string;
}

function ActionBanner({ status, requestNumber }: ActionBannerProps) {
  if (status === 'awaitingPayment') {
    return (
      <Link
        to={`/portal/track/${requestNumber}`}
        style={{ textDecoration: 'none' }}
      >
        <div style={{
          marginTop: 10, padding: '9px 12px', borderRadius: 7,
          background: 'var(--cds-orange-50)', border: '1px solid var(--cds-yellow-200)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <IconCreditCard size={14} style={{ color: 'var(--cds-yellow-600)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--cds-yellow-800)', fontWeight: 500, flex: 1 }}>
            Fee payment is due. View your invoice to pay online.
          </span>
          <IconChevronRight size={13} style={{ color: 'var(--cds-yellow-700)', flexShrink: 0 }} />
        </div>
      </Link>
    );
  }
  if (status === 'awaitingRequester') {
    return (
      <Link
        to={`/portal/track/${requestNumber}`}
        style={{ textDecoration: 'none' }}
      >
        <div style={{
          marginTop: 10, padding: '9px 12px', borderRadius: 7,
          background: 'var(--cds-orange-50)', border: '1px solid var(--cds-yellow-200)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <IconMessageCircle size={14} style={{ color: 'var(--cds-yellow-600)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--cds-yellow-800)', fontWeight: 500, flex: 1 }}>
            Your response is needed before we can proceed.
          </span>
          <IconChevronRight size={13} style={{ color: 'var(--cds-yellow-700)', flexShrink: 0 }} />
        </div>
      </Link>
    );
  }
  if (status === 'completed') {
    return (
      <Link
        to={`/portal/track/${requestNumber}`}
        style={{ textDecoration: 'none' }}
      >
        <div style={{
          marginTop: 10, padding: '9px 12px', borderRadius: 7,
          background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <IconCircleCheck size={14} style={{ color: 'var(--cds-green-700)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#166534', fontWeight: 500, flex: 1 }}>
            Your response is ready. Download released records.
          </span>
          <IconChevronRight size={13} style={{ color: 'var(--cds-green-700)', flexShrink: 0 }} />
        </div>
      </Link>
    );
  }
  return null;
}

// ── Login / Register forms ────────────────────────────────

function LoginForm() {
  const { login, loading, error } = usePublicAuthStore();
  const [form, setForm] = useState({ email: '', password: '' });
  const [localErr, setLocalErr] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalErr('');
    try {
      await login(form.email, form.password);
    } catch (err) {
      setLocalErr(err instanceof Error ? err.message : 'Login failed');
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {(error || localErr) && (
        <div style={{ padding: '10px 12px', background: 'var(--cds-red-50)', border: '1px solid var(--cds-red-200)', borderRadius: 6, fontSize: 13, color: 'var(--cds-red-700)' }}>
          {error || localErr}
        </div>
      )}
      <div className="form-field" style={{ marginBottom: 0 }}>
        <label className="form-label">Email Address</label>
        <input type="email" className="form-input" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
      </div>
      <div className="form-field" style={{ marginBottom: 0 }}>
        <label className="form-label">Password</label>
        <input type="password" className="form-input" required value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
      </div>
      <button type="submit" className="btn btn-primary" disabled={loading} style={{ gap: 6, justifyContent: 'center' }}>
        {loading ? <IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : 'Sign In'}
      </button>
    </form>
  );
}

function RegisterForm({ onSuccess }: { onSuccess: () => void }) {
  const { register, loading, error } = usePublicAuthStore();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirm: '' });
  const [localErr, setLocalErr] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalErr('');
    if (form.password !== form.confirm) { setLocalErr('Passwords do not match.'); return; }
    if (form.password.length < 8) { setLocalErr('Password must be at least 8 characters.'); return; }
    try {
      await register(form.email, form.password, form.firstName, form.lastName);
      onSuccess();
    } catch (err) {
      setLocalErr(err instanceof Error ? err.message : 'Registration failed');
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {(error || localErr) && (
        <div style={{ padding: '10px 12px', background: 'var(--cds-red-50)', border: '1px solid var(--cds-red-200)', borderRadius: 6, fontSize: 13, color: 'var(--cds-red-700)' }}>
          {error || localErr}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-field" style={{ marginBottom: 0 }}>
          <label className="form-label required">First Name</label>
          <input type="text" className="form-input" required value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
        </div>
        <div className="form-field" style={{ marginBottom: 0 }}>
          <label className="form-label required">Last Name</label>
          <input type="text" className="form-input" required value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
        </div>
      </div>
      <div className="form-field" style={{ marginBottom: 0 }}>
        <label className="form-label required">Email Address</label>
        <input type="email" className="form-input" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
      </div>
      <div className="form-field" style={{ marginBottom: 0 }}>
        <label className="form-label required">Password</label>
        <input type="password" className="form-input" required minLength={8} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
      </div>
      <div className="form-field" style={{ marginBottom: 0 }}>
        <label className="form-label required">Confirm Password</label>
        <input type="password" className="form-input" required value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
      </div>
      <button type="submit" className="btn btn-primary" disabled={loading} style={{ gap: 6, justifyContent: 'center' }}>
        {loading ? <IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : 'Create Account'}
      </button>
    </form>
  );
}

// ── Logged-in dashboard ───────────────────────────────────

function Dashboard() {
  const { user, logout } = usePublicAuthStore();
  const [requests, setRequests] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!user?.email) return;
    getMyRequests(user.email)
      .then(setRequests)
      .finally(() => setLoading(false));
  }, [user]);

  const actionItems = requests.filter(r => ACTION_STATUSES.has(String(r[F.status] ?? '')));
  const completedNew = requests.filter(r => String(r[F.status] ?? '') === 'completed');

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-primary-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconUser size={18} style={{ color: 'var(--color-primary-700)' }} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-neutral-900)' }}>{user?.firstName} {user?.lastName}</div>
            <div style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{user?.email}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/portal/submit" className="btn btn-primary btn-sm" style={{ gap: 4 }}>
            <IconFileText size={13} /> New Request
          </Link>
          <button className="btn btn-secondary btn-sm" onClick={logout}>Sign Out</button>
        </div>
      </div>

      {/* Action items summary banner */}
      {!loading && (actionItems.length > 0 || completedNew.length > 0) && (
        <div style={{
          marginBottom: 20, padding: '14px 16px',
          background: 'var(--cds-yellow-50)', border: '1px solid var(--cds-yellow-300)', borderRadius: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: actionItems.length > 0 ? 10 : 0 }}>
            <IconAlertTriangle size={15} style={{ color: 'var(--cds-yellow-600)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--cds-yellow-800)' }}>
              {actionItems.length > 0
                ? `${actionItems.length} request${actionItems.length !== 1 ? 's' : ''} need${actionItems.length === 1 ? 's' : ''} your attention`
                : `${completedNew.length} completed request${completedNew.length !== 1 ? 's have' : ' has'} records available`
              }
            </span>
          </div>
          {actionItems.map(r => {
            const status = String(r[F.status] ?? '');
            const num    = String(r[F.requestNumber] ?? '');
            return (
              <Link key={r['record_id'] as number} to={`/portal/track/${num}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0',
                  borderTop: '1px solid var(--cds-yellow-200)',
                }}>
                  {status === 'awaitingPayment'
                    ? <IconCreditCard size={13} style={{ color: 'var(--cds-yellow-600)', flexShrink: 0 }} />
                    : <IconMessageCircle size={13} style={{ color: 'var(--cds-yellow-600)', flexShrink: 0 }} />
                  }
                  <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--cds-yellow-700)', flexShrink: 0 }}>{num}</span>
                  <span style={{ fontSize: 12, color: 'var(--cds-yellow-800)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {String(r[F.subject] ?? '')}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--cds-yellow-700)', flexShrink: 0, fontWeight: 600 }}>
                    {status === 'awaitingPayment' ? 'Pay now →' : 'Respond →'}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-neutral-800)', margin: '0 0 14px' }}>My Requests</h2>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
          <IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : requests.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 13 }}>
          <div style={{ marginBottom: 12 }}>You haven't submitted any requests yet.</div>
          <Link to="/portal/submit" className="btn btn-primary btn-sm">Submit Your First Request</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map(r => {
            const status = String(r[F.status] ?? '');
            const num    = String(r[F.requestNumber] ?? '');
            const ss = STATUS_STYLE[status] ?? { bg: 'var(--cds-gray-50)', text: 'var(--color-secondary-700)' };
            const needsAction = ACTION_STATUSES.has(status);
            return (
              <div
                key={r['record_id'] as number}
                className="card"
                style={{
                  padding: '14px 16px',
                  borderColor: needsAction ? 'var(--cds-yellow-300)' : undefined,
                  background: needsAction ? 'var(--cds-yellow-50)' : undefined,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--color-primary-700)' }}>
                        {num}
                      </span>
                      <span style={{ padding: '1px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: ss.bg, color: ss.text }}>
                        {STATUS_LABELS[status] ?? status}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-800)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(r[F.subject] ?? '')}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
                      Submitted {r[F.dateSubmitted] ? formatDate(String(r[F.dateSubmitted])) : '—'}
                    </div>
                  </div>
                  <Link
                    to={`/portal/track/${num}`}
                    style={{ fontSize: 12, color: 'var(--color-primary-700)', textDecoration: 'none', flexShrink: 0, paddingTop: 2 }}
                  >
                    Track →
                  </Link>
                </div>
                <ActionBanner status={status} requestNumber={num} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const user = usePublicAuthStore(s => s.user);
  const [mode, setMode] = useState<Mode>('login');

  if (user) return <Dashboard />;

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', padding: '40px 24px' }}>
      <div style={{ marginBottom: 20 }}>
        <Link to="/portal" style={{ color: 'var(--color-primary-700)', textDecoration: 'none', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <IconArrowLeft size={13} /> Back to Portal
        </Link>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--color-neutral-200)', marginBottom: 24 }}>
        {(['login', 'register'] as const).map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            style={{
              padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: mode === m ? 600 : 400,
              color: mode === m ? 'var(--color-primary-700)' : 'var(--color-neutral-500)',
              borderBottom: mode === m ? '2px solid var(--color-primary-700)' : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {m === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        ))}
      </div>

      {mode === 'login'    && <LoginForm />}
      {mode === 'register' && <RegisterForm onSuccess={() => {}} />}
    </div>
  );
}
